import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, ArrowRight, Search, RotateCcw, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useLanguage } from '../i18n';
import { useAuth, api } from '../contexts/AuthContext';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { EditableText } from '../components/InlineEdit';

const FALLBACK_IMG = 'https://images.pexels.com/photos/5659012/pexels-photo-5659012.jpeg';

// Carrossel automático de fotos do serviço (setas + pontinhos + autoplay)
const ServiceImageCarousel = ({ images, name, fit = 'cover' }) => {
  const pics = (images && images.length > 0) ? images : [FALLBACK_IMG];
  const [current, setCurrent] = useState(0);
  const hasMultiple = pics.length > 1;
  const isContain = fit === 'contain';

  useEffect(() => {
    if (!hasMultiple) return;
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % pics.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [hasMultiple, pics.length]);

  const go = (e, dir) => {
    e.stopPropagation();
    setCurrent((prev) => (prev + dir + pics.length) % pics.length);
  };

  return (
    <div className={`relative w-full h-full ${isContain ? 'bg-[#F2F0EB]' : ''}`} data-testid="service-carousel">
      {pics.map((src, i) => (
        <img
          key={i}
          src={src}
          alt={name}
          className={`absolute inset-0 w-full h-full transition-all duration-700 ${isContain ? 'object-contain p-2' : 'object-cover group-hover:scale-110'} ${i === current ? 'opacity-100' : 'opacity-0'}`}
          onError={(e) => { e.target.src = FALLBACK_IMG; }}
        />
      ))}
      {hasMultiple && (
        <>
          <button
            type="button"
            data-testid="carousel-prev"
            onClick={(e) => go(e, -1)}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center shadow-md hover:bg-white transition-colors"
          >
            <ChevronLeft className="w-4 h-4 text-[#1A1815]" />
          </button>
          <button
            type="button"
            data-testid="carousel-next"
            onClick={(e) => go(e, 1)}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center shadow-md hover:bg-white transition-colors"
          >
            <ChevronRight className="w-4 h-4 text-[#1A1815]" />
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10 flex gap-1.5">
            {pics.map((_, i) => (
              <button
                key={i}
                type="button"
                data-testid={`carousel-dot-${i}`}
                onClick={(e) => { e.stopPropagation(); setCurrent(i); }}
                className={`rounded-full transition-all ${i === current ? 'w-5 h-2 bg-white' : 'w-2 h-2 bg-white/60'}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Sub-componente para o Card com efeito de Giro
const ServiceCard = ({ service, index, language, t, primaryColor, secondaryColor, textColor, getCategoryName, handleBookService }) => {
  const [isFlipped, setIsFlipped] = useState(false);

  const name = language === 'pt' ? service.name_pt : service.name_en;
  const description = language === 'pt' ? service.description_pt : service.description_en;

  return (
    <div 
      className="relative h-[480px] w-full perspective-1000 group"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      <div 
        className={`relative w-full h-full transition-all duration-700 preserve-3d ${isFlipped ? 'rotate-y-180' : ''}`}
      >
        {/* LADO A: FRENTE (Com Foto) */}
        <div className="absolute inset-0 backface-hidden bg-white rounded-2xl overflow-hidden border border-[#E6E2DA] flex flex-col shadow-sm">
          <div className="relative h-64 overflow-hidden">
            <ServiceImageCarousel images={service.images} name={name} fit={service.image_fit} />
            <div className="absolute top-4 left-4">
              <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs uppercase tracking-widest text-[#635E54]">
                {getCategoryName(service.category)}
              </span>
            </div>
            <div className="absolute bottom-4 right-4">
              <span className="bg-[#1A1815] text-white px-4 py-2 rounded-full font-semibold shadow-lg">
                €{service.price}
              </span>
            </div>
          </div>

          <div className="p-6 flex flex-col flex-grow">
            <h3 className="font-heading text-xl font-medium text-[#1A1815] line-clamp-1">
              {name}
            </h3>
            <p className="text-[#635E54] text-sm mt-2 line-clamp-2 flex-grow">
              {description}
            </p>
            
            <button 
              onClick={() => setIsFlipped(true)}
              className="text-xs font-semibold uppercase tracking-wider mt-2 mb-4 text-left hover:opacity-70 transition-opacity"
              style={{ color: primaryColor }}
            >
              {language === 'pt' ? '+ Ler mais' : '+ Read more'}
            </button>

            <div className="flex items-center gap-3 text-sm mb-4">
              <span className="flex items-center gap-1 text-[#A8A29A]">
                <Clock className="w-4 h-4" />
                {service.duration} {t('services.minutes')}
              </span>
              
              {/* DISPLAY DOS PONTOS NA FRENTE */}
              <span className="bg-amber-50 text-amber-700 text-[10px] px-2 py-0.5 rounded-full font-bold border border-amber-100 flex items-center gap-1">
                <Star className="w-3 h-3 fill-amber-400 stroke-amber-500" />
                {service.loyalty_points || 0} PTS
              </span>
            </div>

            <Button
              onClick={() => handleBookService(service.service_id)}
              className="w-full bg-[#1A1815] text-white hover:bg-[#2C2924] rounded-full mt-auto"
            >
              {t('services.book_now')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        {/* LADO B: VERSO (Descrição e Pontos Detalhados) */}
        <div className="absolute inset-0 backface-hidden rotate-y-180 bg-white rounded-2xl overflow-hidden border border-[#BFA05F]/30 flex flex-col p-8 shadow-xl">
          <div className="flex justify-between items-start mb-4 text-left">
            <div>
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#A8A29A]">
                {getCategoryName(service.category)}
              </span>
              <h3 className="font-heading text-2xl font-medium text-[#1A1815] mt-1">
                {name}
              </h3>
            </div>
            <div className="flex flex-col items-end">
              <span className="text-xl font-semibold text-[#1A1815]">€{service.price}</span>
              {/* DISPLAY DOS PONTOS NO VERSO */}
              <span className="text-[10px] font-bold text-amber-600 mt-1 flex items-center gap-1">
                +{service.loyalty_points || 0} PTS
              </span>
            </div>
          </div>

          <div className="flex-grow overflow-y-auto pr-2 custom-scrollbar text-left">
            <p className="text-[#635E54] text-sm leading-relaxed whitespace-pre-line">
              {description}
            </p>
          </div>

          <div className="mt-6 space-y-4">
            <div className="flex items-center justify-between text-sm border-t border-[#E6E2DA] pt-4">
               <span className="flex items-center gap-1 text-[#A8A29A]">
                <Clock className="w-4 h-4" />
                {service.duration} {t('services.minutes')}
              </span>
              <button 
                onClick={() => setIsFlipped(false)}
                className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest hover:opacity-70 transition-opacity"
                style={{ color: primaryColor }}
              >
                <RotateCcw className="w-3 h-3" />
                {language === 'pt' ? 'Voltar' : 'Back'}
              </button>
            </div>
            
            <Button
              onClick={() => handleBookService(service.service_id)}
              className="w-full bg-[#1A1815] text-white hover:bg-[#2C2924] rounded-full"
            >
              {t('services.book_now')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const { t, language } = useLanguage();
  const { user, isAuthenticated, login } = useAuth();
  const { settings } = useSiteSettings();
  const navigate = useNavigate();

  const primaryColor = settings?.primary_color || '#BFA05F';
  const secondaryColor = settings?.secondary_color || '#1A1815';
  const backgroundColor = settings?.background_color || '#F9F8F6';
  const textColor = settings?.text_color || '#1A1815';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [servicesRes, categoriesRes] = await Promise.all([
        api.get('/services'),
        api.get('/categories')
      ]);
      setServices(servicesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const categoryOptions = [
    { id: 'all', label: t('services.categories.all') },
    ...categories.map(cat => ({
      id: cat.slug,
      label: language === 'pt' ? cat.name_pt : (cat.name_en || cat.name_pt)
    }))
  ];

  const getCategoryName = (categorySlug) => {
    const cat = categories.find(c => c.slug === categorySlug);
    if (cat) return language === 'pt' ? cat.name_pt : (cat.name_en || cat.name_pt);
    return t(`services.categories.${categorySlug}`);
  };

  const filteredServices = services.filter((service) => {
    const matchesCategory = selectedCategory === 'all' || service.category === selectedCategory;
    const name = (language === 'pt' ? service.name_pt : service.name_en) || '';
    return matchesCategory && name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleBookService = (serviceId) => {
    if (isAuthenticated) navigate(`/booking?service=${serviceId}`);
    else login();
  };

  return (
    <div className="min-h-screen pt-24" style={{ backgroundColor }}>
      <style>{`
        .perspective-1000 { perspective: 1000px; }
        .preserve-3d { transform-style: preserve-3d; }
        .backface-hidden { backface-visibility: hidden; }
        .rotate-y-180 { transform: rotateY(180deg); }
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #E6E2DA; border-radius: 10px; }
        .h-scrollbar::-webkit-scrollbar { height: 6px; }
        .h-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .h-scrollbar::-webkit-scrollbar-thumb { background: #D8D2C6; border-radius: 10px; }
        .h-scrollbar::-webkit-scrollbar-thumb:hover { background: #BFA05F; }
        .h-scrollbar { scrollbar-width: thin; scrollbar-color: #D8D2C6 transparent; }
      `}</style>

      {/* Header */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 text-center">
          <span className="text-sm uppercase tracking-widest font-medium" style={{ color: primaryColor }}>
            <EditableText settingKey="brand_name" defaultValue="Lou's Way" />
          </span>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight mt-4" style={{ color: textColor }}>
            <EditableText settingKey={`services_page_title_${language}`} defaultValue={t('services.title')} />
          </h1>
          <p className="text-lg mt-6 leading-relaxed max-w-3xl mx-auto" style={{ color: `${textColor}80` }}>
            <EditableText settingKey={`services_page_subtitle_${language}`} defaultValue={t('services.subtitle')} multiline />
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-[70px] md:top-20 z-30 border-b border-[#E6E2DA] bg-white shadow-sm" style={{ backgroundColor }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-4 md:py-8 flex flex-col md:flex-row gap-6 items-center justify-between">
          <div className="flex w-full md:flex-1 md:min-w-0 overflow-x-auto pb-2 gap-2 h-scrollbar">
            {categoryOptions.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-sm whitespace-nowrap border transition-all ${
                  selectedCategory === cat.id ? 'text-white border-transparent' : 'bg-white border-[#E6E2DA]'
                }`}
                style={selectedCategory === cat.id ? { backgroundColor: secondaryColor } : { color: `${textColor}80` }}
              >
                {cat.label}
              </button>
            ))}
          </div>
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8A29A]" />
            <Input
              placeholder={t('common.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-full border-[#E6E2DA]"
            />
          </div>
        </div>
      </section>

      {/* Grid de Serviços */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => <div key={i} className="h-[480px] bg-white rounded-2xl animate-pulse" />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredServices.map((service, index) => (
                <ServiceCard 
                  key={service.service_id}
                  service={service}
                  index={index}
                  language={language}
                  t={t}
                  primaryColor={primaryColor}
                  secondaryColor={secondaryColor}
                  textColor={textColor}
                  getCategoryName={getCategoryName}
                  handleBookService={handleBookService}
                />
              ))}
            </div>
          )}
          {!loading && filteredServices.length === 0 && (
            <div className="text-center py-16 text-[#635E54]">{t('common.no_results')}</div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;